import * as React from 'react';

import type { ToolCallHistoryStep } from '@/stores/local/shared/ToolCallHistoryStore/types';

type UseToolCallStepsExpandReturn = {
  isStepNew: (stepId: string) => boolean;
  isStepExpanded: (stepId: string) => boolean;
  handleStepHeaderClick: (e: React.MouseEvent<Element>) => void;
};

export function useToolCallStepsExpand(steps: ToolCallHistoryStep[]): UseToolCallStepsExpandReturn {
  const seenStepsRef = React.useRef<Set<string>>(new Set());
  const [expandedStepIds, setExpandedStepIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setExpandedStepIds((prev) => {
      const next = new Set(prev);

      steps.forEach((step) => {
        if (!seenStepsRef.current.has(step.id)) {
          next.add(step.id);
          seenStepsRef.current.add(step.id);
        }
      });

      return next;
    });
  }, [steps]);

  const toggleStep = React.useCallback((stepId: string) => {
    setExpandedStepIds((prev) => {
      const next = new Set(prev);

      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }

      return next;
    });
  }, []);

  const handleStepHeaderClick = React.useCallback(
    (e: React.MouseEvent<Element>) => {
      const stepId = (e.currentTarget as HTMLElement).getAttribute('data-step-id');

      if (stepId) {
        toggleStep(stepId);
      }
    },
    [toggleStep],
  );

  const isStepNew = React.useCallback((stepId: string) => !seenStepsRef.current.has(stepId), []);

  const isStepExpanded = React.useCallback((stepId: string) => expandedStepIds.has(stepId), [expandedStepIds]);

  return { isStepNew, isStepExpanded, handleStepHeaderClick };
}
